import { isEqual } from 'lodash';

export type TListKeyer<I> = (item: I) => string;

// One keyer per list-valued key that should merge item by item; `String` turns a plain list into a set
export type TListKeyers<T> = {
  [K in keyof T]?: T[K] extends readonly (infer I)[] ? TListKeyer<I> : never;
};

export type TMergeResult<T> = {
  merged: T;
  // What both sides changed, where the draft's version was kept: a key, or `key.itemKey` in a list
  conflicts: string[];
};

// Three-way merge of a draft onto a newer server version; `base` is what the draft started from.
// Per key, a change made only in the draft is kept, a change made only on the server is taken,
// and when both changed it the draft's value is kept and the key reported. Keys in `lists` merge
// item by item on the item key: items the draft added or changed are kept, items it removed
// stay removed, everything else follows the server.
export const mergeDrafts = <T extends object>(
  base: T,
  draft: T,
  server: T,
  lists: TListKeyers<T> = {}
): TMergeResult<T> => {
  const merged: T = { ...server };
  const conflicts: string[] = [];
  for (const key of Object.keys(draft) as Array<keyof T & string>) {
    const baseValue = base[key];
    const draftValue = draft[key];
    if (isEqual(draftValue, baseValue)) continue;

    const serverValue = server[key];
    const keyOf = lists[key];
    if (
      keyOf &&
      Array.isArray(baseValue) &&
      Array.isArray(draftValue) &&
      Array.isArray(serverValue)
    ) {
      const result = mergeLists(baseValue, draftValue, serverValue, keyOf);
      // TypeScript cannot relate T[K] to the keyer's item type; both are arrays of the same items
      merged[key] = result.items as T[typeof key];
      conflicts.push(...result.conflicts.map((itemKey) => `${key}.${itemKey}`));
      continue;
    }

    merged[key] = draftValue;
    if (!isEqual(serverValue, baseValue) && !isEqual(serverValue, draftValue)) {
      conflicts.push(key);
    }
  }
  return { merged, conflicts };
};

const mergeLists = <I>(
  base: I[],
  draft: I[],
  server: I[],
  keyOf: TListKeyer<I>
) => {
  const byKey = (items: I[]) =>
    new Map(items.map((item) => [keyOf(item), item] as const));
  const baseByKey = byKey(base);
  const draftByKey = byKey(draft);
  const serverByKey = byKey(server);
  const items: I[] = [];
  const conflicts: string[] = [];

  // The server's items in its order, minus what the draft removed, with the draft's changes
  for (const [key, serverItem] of serverByKey) {
    const baseItem = baseByKey.get(key);
    const draftItem = draftByKey.get(key);
    if (draftItem === undefined) {
      // Removed by the draft, or never in the draft: the server's addition stays
      if (baseItem === undefined) items.push(serverItem);
      continue;
    }
    const draftChanged = baseItem === undefined || !isEqual(draftItem, baseItem);
    if (!draftChanged) {
      items.push(serverItem);
      continue;
    }
    items.push(draftItem);
    const serverChanged = baseItem === undefined || !isEqual(serverItem, baseItem);
    if (serverChanged && !isEqual(serverItem, draftItem)) conflicts.push(key);
  }

  // What the draft added or changed and the server no longer has
  for (const [key, draftItem] of draftByKey) {
    if (serverByKey.has(key)) continue;
    const baseItem = baseByKey.get(key);
    if (baseItem === undefined) {
      items.push(draftItem);
    } else if (!isEqual(draftItem, baseItem)) {
      items.push(draftItem);
      conflicts.push(key);
    }
  }

  return { items, conflicts };
};
